import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  type PolicyDocumentReview,
  PolicyDocumentReviewStatus,
  usePolicyDocumentReview,
} from '../../../../../models/insurancePolicy'
import { type PolicyPrefill } from './PolicyFormDialog'

interface PolicyDocumentReviewWatcherProps {
  orgId: string
  policyId: string
  /** From the upload response — identifies the review this watcher waits for. */
  token: string
  onPreviewChanges: (prefill: PolicyPrefill) => void
  onSettled: () => void
}

function offerChanges(review: PolicyDocumentReview, onPreview: (p: PolicyPrefill) => void) {
  toast.custom(
    (t) => (
      <div className="bg-background flex items-center gap-3 rounded-md border p-4 shadow-lg">
        <span className="text-sm font-medium">We detected changes in the uploaded policy document.</span>
        <Button
          size="sm"
          onClick={() => {
            onPreview({ vendor: review.vendor ?? null, coverages: review.coverages ?? [] })
            toast.dismiss(t)
          }}
        >
          Preview changes
        </Button>
      </div>
    ),
    { duration: 15000 }
  )
}

/**
 * Waits for the LLM to finish diffing an uploaded policy document against the
 * policy on file, then offers the changes for review. Renders nothing.
 */
export function PolicyDocumentReviewWatcher({
  orgId,
  policyId,
  token,
  onPreviewChanges,
  onSettled,
}: PolicyDocumentReviewWatcherProps) {
  const { review } = usePolicyDocumentReview(orgId, policyId, token)
  const handled = useRef(false)
  const callbacks = useRef({ onPreviewChanges, onSettled })
  callbacks.current = { onPreviewChanges, onSettled }

  useEffect(() => {
    if (handled.current || !review || review.status === PolicyDocumentReviewStatus.PENDING) return
    handled.current = true
    if (review.status === PolicyDocumentReviewStatus.READY && review.hasChanges) {
      offerChanges(review, callbacks.current.onPreviewChanges)
    }
    callbacks.current.onSettled()
  }, [review])

  return null
}
